/**
 * Hourly profile — the day-select detail beneath the deep Sankey.
 *
 * For the focused end-use on the selected day, plots per-hour ACTUAL metered
 * load vs the EXPECTED curve (the site's own weather-adjusted load shape during
 * operating hours, dropping to the setback floor when closed). The band between
 * them = observed after-hours potential savings. The scheduled operating window
 * is drawn as
 * a shaded band with "Opens"/"Closes" markers. Custom SVG (no charting dep); the
 * actual line carries the end-use's canonical color.
 *
 * Copy contract: OBSERVED framing only — the figure is money already spent,
 * so no projected/forecast/will-reduce language.
 */
import type React from "react"

import { FONT_WEIGHT } from "@/models/typography"

import { Box, CircularProgress, Stack, Typography } from "@mui/material"

import { useHourlyProfile } from "../../data/use-hourly-profile"
import { Eyebrow } from "../../homepage/atoms"
import { enduseColor, enduseLabel } from "../variance-route/enduse-colors"
import { ChartDataTable } from "./chart-data-table"
import type { HourlyPoint } from "./schedule-misalignment.types"
import { useFormatWasteCurrency } from "./use-format-waste-currency"

const MONO = "ui-monospace, 'SF Mono', Menlo, Consolas, monospace"
const VB_W = 720
const VB_H = 248
const PAD_L = 46
const PAD_R = 18
const PAD_T = 26
const PAD_B = 30

const xHourLabel = (h: number): string => {
  const ampm = h < 12 || h === 24 ? "a" : "p"
  const h12 = h % 12 || 12
  return `${h12}${ampm}`
}

const clockLabel = (h: number): string => {
  const hr = Math.round(h)
  const ampm = hr < 12 || hr === 24 ? "AM" : "PM"
  const h12 = hr % 12 || 12
  return `${h12} ${ampm}`
}

// Bordered rounded panel the chart / its states sit on (matches the Sankey
// surface). Module-level so it isn't re-created each render.
const Surface: React.FC<React.PropsWithChildren> = ({ children }) => (
  <Box
    sx={{
      mt: 2,
      borderRadius: 3,
      border: 1,
      borderColor: "grey.200",
      bgcolor: "grey.25",
      p: 2.5,
    }}
  >
    {children}
  </Box>
)

const LegendItem: React.FC<{
  children: React.ReactNode
  swatch: React.ReactNode
}> = ({ children, swatch }) => (
  <Stack direction="row" spacing={0.75} sx={{ alignItems: "center" }}>
    {swatch}
    <Typography color="text.secondary" variant="body2">
      {children}
    </Typography>
  </Stack>
)

export const HourlyProfileChart: React.FC<{
  siteId: string
  enduse: string
  day: string
}> = ({ siteId, enduse, day }) => {
  const formatCurrency = useFormatWasteCurrency()
  const { data, isPending, isError } = useHourlyProfile(siteId, enduse, day)
  const color = enduseColor(enduse)

  if (isPending) {
    return (
      <Surface>
        <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
          <CircularProgress size={16} />
          <Typography color="text.secondary" variant="body2">
            Loading hourly detail…
          </Typography>
        </Stack>
      </Surface>
    )
  }

  const points: HourlyPoint[] =
    isError || !data?.available
      ? []
      : [...data.points].sort((a, b) => a.hour - b.hour)

  if (points.length === 0) {
    return (
      <Surface>
        <Typography color="text.secondary" variant="body2">
          No hourly detail for this day.
        </Typography>
      </Surface>
    )
  }

  const plotW = VB_W - PAD_L - PAD_R
  const plotH = VB_H - PAD_T - PAD_B
  const maxRaw = Math.max(
    ...points.map((p) => Math.max(p.actual_kw, p.expected_kw)),
    1
  )
  // round the axis top up to a tidy number
  const step = Math.pow(10, Math.floor(Math.log10(maxRaw))) / 2
  const maxKw = Math.ceil(maxRaw / step) * step
  const px = (hour: number): number => PAD_L + (hour / 23) * plotW
  const py = (kw: number): number => PAD_T + plotH - (kw / maxKw) * plotH
  const axisY = PAD_T + plotH

  const actualPts = points
    .map((p) => `${px(p.hour)},${py(p.actual_kw)}`)
    .join(" ")
  const expectedPts = points
    .map((p) => `${px(p.hour)},${py(p.expected_kw)}`)
    .join(" ")
  // area under the actual line (down to the axis) + the waste band (actual→expected)
  const actualArea = `${px(points[0].hour)},${axisY} ${actualPts} ${px(points[points.length - 1].hour)},${axisY}`
  const wasteArea = [
    ...points.map((p) => `${px(p.hour)},${py(p.actual_kw)}`),
    ...[...points].reverse().map((p) => `${px(p.hour)},${py(p.expected_kw)}`),
  ].join(" ")

  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((f) => f * maxKw)
  const xTicks = [0, 4, 8, 12, 16, 20, 23]
  const openH = data?.open_hour ?? null
  const closeH = data?.close_hour ?? null

  return (
    <Surface>
      <Stack
        direction="row"
        spacing={1}
        sx={{ alignItems: "baseline", justifyContent: "space-between", mb: 1 }}
      >
        <Box>
          <Eyebrow>Hourly load · observed</Eyebrow>
          <Typography
            sx={{ fontWeight: FONT_WEIGHT.bold, letterSpacing: "-0.2px" }}
            variant="body1"
          >
            {enduseLabel(enduse)} · {day}
          </Typography>
        </Box>
        <Box sx={{ textAlign: "right" }}>
          <Typography
            sx={{
              fontFamily: MONO,
              fontWeight: FONT_WEIGHT.bold,
              color: "text.primary",
              lineHeight: 1.1,
            }}
            variant="body1"
          >
            {formatCurrency(data?.waste_dollars_total ?? 0)}
          </Typography>
          <Eyebrow>after-hours potential savings identified</Eyebrow>
        </Box>
      </Stack>

      {/* Legend */}
      <Stack
        direction="row"
        spacing={2}
        sx={{ alignItems: "center", flexWrap: "wrap", rowGap: 0.5, mb: 0.5 }}
      >
        <LegendItem
          swatch={
            <Box
              sx={{ width: 16, height: 3, borderRadius: 2, bgcolor: color }}
            />
          }
        >
          Actual
        </LegendItem>
        <LegendItem
          swatch={
            <Box
              sx={{
                width: 16,
                height: 0,
                borderTop: "2px dashed",
                borderColor: "grey.500",
              }}
            />
          }
        >
          Expected
        </LegendItem>
        <LegendItem
          swatch={
            <Box
              sx={{
                width: 14,
                height: 12,
                borderRadius: 0.5,
                bgcolor: color,
                opacity: 0.18,
              }}
            />
          }
        >
          Potential savings
        </LegendItem>
        {openH != null && closeH != null ? (
          <LegendItem
            swatch={
              <Box
                sx={{
                  width: 14,
                  height: 12,
                  borderRadius: 0.5,
                  bgcolor: "primary.main",
                  opacity: 0.08,
                  border: "1px dashed",
                  borderColor: "primary.main",
                }}
              />
            }
          >
            Operating hours
          </LegendItem>
        ) : null}
      </Stack>

      <Box
        component="svg"
        aria-label={`Hourly actual vs expected load for ${enduseLabel(enduse)} on ${day}`}
        role="img"
        sx={{ width: "100%", height: "auto", display: "block" }}
        viewBox={`0 0 ${VB_W} ${VB_H}`}
      >
        {/* y gridlines + labels */}
        {yTicks.map((v) => (
          <g key={`y-${v}`}>
            <line
              stroke="#eee"
              x1={PAD_L}
              x2={VB_W - PAD_R}
              y1={py(v)}
              y2={py(v)}
            />
            <text
              fill="#b0b0b0"
              fontFamily={MONO}
              fontSize={9}
              textAnchor="end"
              x={PAD_L - 6}
              y={py(v) + 3}
            >
              {Math.round(v)}
            </text>
          </g>
        ))}
        <text
          fill="#b0b0b0"
          fontFamily={MONO}
          fontSize={8.5}
          fontWeight={700}
          x={4}
          y={PAD_T - 12}
        >
          kW
        </text>

        {/* operating-hours band + open/close markers */}
        {openH != null && closeH != null ? (
          <g>
            <rect
              fill="#6b8c7f"
              height={plotH}
              opacity={0.07}
              width={Math.max(0, px(closeH) - px(openH))}
              x={px(openH)}
              y={PAD_T}
            />
            {[
              { h: openH, label: `Opens ${clockLabel(openH)}` },
              { h: closeH, label: `Closes ${clockLabel(closeH)}` },
            ].map((mk) => (
              <g key={mk.label}>
                <line
                  stroke="#6b8c7f"
                  strokeDasharray="3 3"
                  strokeWidth={1}
                  x1={px(mk.h)}
                  x2={px(mk.h)}
                  y1={PAD_T}
                  y2={axisY}
                />
                <text
                  fill="#5a7469"
                  fontFamily={MONO}
                  fontSize={8.5}
                  fontWeight={700}
                  textAnchor="middle"
                  x={px(mk.h)}
                  y={PAD_T - 4}
                >
                  {mk.label}
                </text>
              </g>
            ))}
          </g>
        ) : null}

        {/* x axis baseline + ticks */}
        <line
          stroke="#d5d5d5"
          x1={PAD_L}
          x2={VB_W - PAD_R}
          y1={axisY}
          y2={axisY}
        />
        {xTicks.map((h) => (
          <text
            key={`xt-${h}`}
            fill="#aaa"
            fontFamily={MONO}
            fontSize={9}
            textAnchor="middle"
            x={px(h)}
            y={VB_H - 10}
          >
            {xHourLabel(h)}
          </text>
        ))}

        {/* actual area fill */}
        <polygon fill={color} opacity={0.1} points={actualArea} />
        {/* waste band */}
        <polygon fill={color} opacity={0.2} points={wasteArea} />
        {/* expected (dashed) */}
        <polyline
          fill="none"
          points={expectedPts}
          stroke="#8a8a8a"
          strokeDasharray="5 4"
          strokeWidth={1.5}
        />
        {/* actual */}
        <polyline
          fill="none"
          points={actualPts}
          stroke={color}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2.5}
        />
      </Box>

      <ChartDataTable
        caption={`${enduseLabel(
          enduse
        )} actual against expected load on ${day}, hour by hour`}
        columns={[
          "Hour",
          "Actual kW",
          "Expected kW",
          "Potential savings",
          "Schedule",
        ]}
        rows={points.map((p) => [
          `${String(p.hour).padStart(2, "0")}:00`,
          Math.round(p.actual_kw),
          Math.round(p.expected_kw),
          formatCurrency(p.waste_dollars),
          p.occupied ? "Open" : "Closed",
        ])}
      />
    </Surface>
  )
}
